import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import classes from "./classes.module.scss";
import classNames from "classnames";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { container } from "tsyringe";
import FileService from "services/FileService";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import LazyImage from "components/elements/LazyImage";
import DefaultAvatar from "assets/images/default-avatar.svg";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { format } from "date-fns";
import I18nStore from "components/materials/I18n/I18nStore";
import { match } from "ts-pattern";
import StatusIcon from "components/materials/StatusIcon";
import Social from "components/materials/SocialButton";
import { useNavigate } from "react-router-dom";
import ModuleConfig from "configs/ModuleConfig";
import { useCallback, useEffect, useState } from "react";
import { EAnchorStatus } from "common/enums/Anchor/EAnchorStatus";
import { PencilSquareIcon, ArchiveBoxArrowDownIcon } from "@heroicons/react/20/solid";
import AnchorProjectModal from "components/materials/AnchorProjectModal";
import DownloadProofButton from "components/materials/DownloadProofButton";
import PosterImage from "components/materials/PosterImage";
import ProjectService from "services/ProjectService";
import { useProjectLeader } from "components/materials/ProjectCard";
import { EProjectFilter } from "common/enums/Project/EProjectFilter";
import ConfigService from "services/projects/ConfigService";

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	onChangeGeneratedPosterImage: () => void;
	canChangeGeneratedPosterImage: boolean;
	project: ProjectResponseResource;
	onProjectChange: () => void;
	downloadProof: (id: string, title: string) => void;
};

const fileService = container.resolve(FileService);
const projectService = container.resolve(ProjectService);
const configService = container.resolve(ConfigService);
const modules = container.resolve(ModuleConfig).get().modules;

function formatDate(date: Date): string {
	return format(date, "yyyy-MM-dd");
}

export default function DrawerPro(props: IProps) {
	const navigate = useNavigate();
	const { projectLeader, projectLeaderAvatar } = useProjectLeader(props.project);

	const [isModalOpen, setIsModalOpen] = useState(false);

	const hasSocials = () => {
		return props.project.x || props.project.discord || props.project.instagram || props.project.linkedin;
	};

	const getPosterUrl = async (project: ProjectResponseResource) => {
		return project.poster?.file ? fileService.getFileUrl("POSTER", project.poster.file.id) : null;
	};

	const archiveProject = useCallback(async () => {
		try {
			if (!props.project) return;
			await projectService.archive(props.project.id);
			props.onClose();
		} catch (error) {
			console.error(error);
		}
	}, [props]);

	const publishProject = useCallback(async () => {
		try {
			if (!props.project) return;
			await configService.publish(props.project.id);
			props.onClose();
		} catch (error) {
			console.error(error);
		}
	}, [props]);

	const publicationDate = () => {
		const createdAt = props.project.createdAt;
		if (!createdAt) return "";

		const locale = match(I18nStore.getInstance().lang)
			.with("fr", () => "fr-FR")
			.with("en", () => "en-US")
			.exhaustive();

		return new Date(formatDate(createdAt)).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
	};

	const getPitch = () => {
		return match(I18nStore.getInstance().lang)
			.with("fr", () => props.project.pitch)
			.with("en", () => props.project.pitch_en)
			.exhaustive();
	};

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen);
	};

	const navigateToEditConfig = () => {
		if (props.project === null) return;
		navigate(modules.pages.EditProjectConfig.props.path.replace(":projectId", props.project.id));
		props.onClose();
	};

	const getProjectProStatus = useCallback(() => {
		if (props.project.archivedAt) {
			return EProjectFilter.ARCHIVED;
		}
		if (props.project.publishedAt) {
			return EProjectFilter.ONLINE;
		}
		return EProjectFilter.DRAFT;
	}, [props]);

	const navigateToProjectPage = useCallback(() => {
		navigate(modules.pages.Project.props.path.replace(":projectId", props.project.id));
	}, [props, navigate]);

	const [isSmallScreen, setIsSmallScreen] = useState(window.outerWidth <= 1440); // Assuming $screen-lg is 1024px

	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.innerWidth <= 1440);
		};
		window.addEventListener("resize", handleResize);
		handleResize();
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	return (
		<div className={classes["root"]}>
			<div
				className={classNames(classes["drawer"], {
					[classes["open"]!]: props.isOpen,
				})}>
				<div className={classes["close-container"]}>
					<XMarkIcon className={classes["close-icon"]} onClick={props.onClose} />
				</div>

				<div className={classes["header"]}>
					<div className={classes["poster-container"]}>
						{props.isOpen && <PosterImage project={props.project} getPosterUrl={getPosterUrl} />}
						{props.canChangeGeneratedPosterImage && (
							<Button variant={EButtonVariant.TEXT} color={EButtonColor.PRIMARY} onClick={props.onChangeGeneratedPosterImage}>
								<ArrowPathIcon />
								<Typography type="p" size="large" weight="medium">
									{I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.poster.regenerate)}
								</Typography>
							</Button>
						)}
					</div>

					<div className={classes["info-container"]}>
						<div className={classes["author-container"]}>
							<Typography type="p" size="medium" weight="bold">
								{I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.project_leader)}
							</Typography>

							{projectLeader && (
								<div className={classes["author"]}>
									<LazyImage src={projectLeaderAvatar ?? DefaultAvatar} alt="Avatar" className={classes["avatar"]} />
									<div className={classes["user"]}>
										<Typography type="p" size="small" weight="bold">
											{projectLeader.firstName} {projectLeader.lastName}
										</Typography>
									</div>
								</div>
							)}
						</div>

						<div className={classes["info"]}>
							<Typography type="p" size="medium" weight="bold">
								{I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.infos.title)}
							</Typography>

							<Typography type="p" size="small" weight="regular" typoFont="secondary">
								{I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.infos.publication_date)} {publicationDate()}
							</Typography>

							<Typography type="p" size="small" weight="regular" typoFont="secondary">
								{I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.infos.genre)}{" "}
								{props.project && I18n.trslt(I18n.asset.enums.EProjectThemes[props.project.theme])}
							</Typography>

							<Typography type="p" size="small" weight="regular" typoFont="secondary">
								{I18n.trslt(I18n.asset.pages.admin_dashboard.drawer.infos.format)}{" "}
								{props.project && I18n.trslt(I18n.asset.enums.EProjectFormats[props.project.format])}
							</Typography>

							<div className={classes["status"]}>
								<StatusIcon status={getProjectProStatus()} withDescription />
							</div>

							<div className={classes["status"]}>
								<StatusIcon status={props.project.scenario?.anchor?.status || EAnchorStatus.QUEUED} withDescription />
							</div>
						</div>
					</div>
				</div>

				<div className={classes["body"]}>
					<Typography type="h1" weight="bold">
						{props.project.title}
					</Typography>
					<Typography type="p" size="medium" weight="regular">
						{getPitch()}
					</Typography>
				</div>

				<div className={classes["social-title"]}>
					{hasSocials() && props.project && <Typography>{I18n.trslt(I18n.asset.pages.my_projects.drawer.button.follow_us)}</Typography>}
					<div className={classes["social-download"]}>{hasSocials() && props.project && <Social project={props.project} />}</div>
				</div>

				<div className={classes["pro-buttons-container"]}>
					<div className={classes["pro-buttons"]}>
						{!props.project.archivedAt && (
							<Button
								color={EButtonColor.PRIMARY}
								variant={EButtonVariant.TEXT}
								onClick={archiveProject}
								fullwidth={isSmallScreen}
								startIcon={<ArchiveBoxArrowDownIcon className={classes["icon"]} />}>
								{I18n.trslt(I18n.asset.pages.my_projects.drawer.button.archive_project)}
							</Button>
						)}
						<Button
							color={EButtonColor.PRIMARY}
							variant={EButtonVariant.TEXT}
							onClick={navigateToEditConfig}
							fullwidth={isSmallScreen}
							startIcon={<PencilSquareIcon className={classes["icon"]} />}>
							{I18n.trslt(I18n.asset.pages.my_projects.drawer.button.edit_project)}
						</Button>
					</div>
					<div className={classes["pro-buttons-2"]}>
						{props.project.scenario?.anchor?.status === EAnchorStatus.VERIFIED_ON_CHAIN ? (
							<DownloadProofButton project={props.project} isSmallScreen={isSmallScreen} />
						) : (
							<Button color={EButtonColor.PRIMARY} variant={EButtonVariant.CONTAINED} onClick={toggleModal} fullwidth={isSmallScreen}>
								{I18n.trslt(I18n.asset.pages.my_projects.drawer.button.anchor)}
							</Button>
						)}
						{!props.project.publishedAt ? (
							<Button color={EButtonColor.PRIMARY} variant={EButtonVariant.CONTAINED} onClick={publishProject} fullwidth={isSmallScreen}>
								{I18n.trslt(I18n.asset.pages.my_projects.drawer.button.publish)}
							</Button>
						) : (
							<Button color={EButtonColor.PRIMARY} variant={EButtonVariant.CONTAINED} onClick={navigateToProjectPage} fullwidth={isSmallScreen}>
								{I18n.trslt(I18n.asset.pages.my_projects.drawer.button.see_project)}
							</Button>
						)}
					</div>
				</div>
			</div>

			{props.isOpen && <div className={classes["overlay"]} onClick={props.onClose} />}
			{props.project && <AnchorProjectModal project={props.project} isOpen={isModalOpen} onClose={toggleModal} onAnchorProject={props.onProjectChange} isAdmin={false} />}
		</div>
	);
}
